import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { useFormApi } from "../useForm";
import LabeledControl from "../utils/LabeledControl";
function getInputType(type) {
    switch (type) {
        case "date":
        case "color":
        case "password":
            return type;
        default:
            return "text";
    }
}
function getInputMode(type) {
    switch (type) {
        case "email":
        case "url":
        case "text":
            return type;
        default:
            return undefined;
    }
}
/**
 * This is the generic input control for a set of `<input>` types or `<textarea>`.
 *
 * For rich text (as HTML or JSON) use `<RichText>` and for numeric inputs use `<NumberInput>`.
 */
function Input({ id, className, langKey, label, defaultValue, type = "text", info = false, autoFocus = false, autoComplete = "off", placeholder, required = false, disabled = false, ...inputProps }) {
    const ref = useRef(null);
    const form = useFormApi();
    const { getValue, setValue } = form;
    const [rawValue, setRawValue] = useState(() => { var _a, _b, _c; return (_c = (_b = (_a = getValue(id)) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : defaultValue) !== null && _c !== void 0 ? _c : ""; });
    useEffect(() => {
        var _a, _b, _c, _d;
        if (autoFocus) {
            (_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.getElementsByTagName("input")[0]) === null || _b === void 0 ? void 0 : _b.focus();
            (_d = (_c = ref.current) === null || _c === void 0 ? void 0 : _c.getElementsByTagName("textarea")[0]) === null || _d === void 0 ? void 0 : _d.focus();
        }
    }, [autoFocus]);
    const value = getValue(id);
    useEffect(() => { var _a; return setRawValue((_a = value === null || value === void 0 ? void 0 : value.toString()) !== null && _a !== void 0 ? _a : ""); }, [value]);
    const handleChange = useCallback((e) => {
        setRawValue(e.target.value);
        if ((e.target.value !== "" && !e.target.validity.valid) ||
            getValue(id) === e.target.value) {
            return;
        }
        setValue(id, e.target.value);
    }, [id, type, getValue, setValue]);
    const pattern = inputProps.pattern;
    const inputPattern = useMemo(() => {
        if (pattern)
            return pattern;
        switch (type) {
            case "url":
                return "https?://.+";
            case "email":
                return ".+@.+\\..+";
            case "date":
                return "d{4}-d{2}-d{2}";
            default:
                return undefined;
        }
    }, [type, pattern]);
    const inputPlaceholder = useMemo(() => {
        if (placeholder)
            return placeholder;
        switch (type) {
            case "url":
                return "https://";
            default:
                return " ";
        }
    }, [type, placeholder]);
    return (React.createElement(LabeledControl, { id: id, langKey: langKey, label: label, info: info, required: required, disabled: disabled, ref: ref, className: `aui-${type} ${className !== null && className !== void 0 ? className : ""}` }, type === "textarea" ? (React.createElement("textarea", { id: id, value: rawValue, onChange: handleChange, placeholder: inputPlaceholder || " ", required: required, disabled: disabled })) : (React.createElement("input", { id: id, type: getInputType(type), ...inputProps, value: rawValue, onChange: handleChange, placeholder: inputPlaceholder || " ", autoComplete: autoComplete, required: required, pattern: inputPattern, disabled: disabled, inputMode: getInputMode(type) }))));
}
export default Input;
